// Los cuatro sonidos de un minijuego: acertar, fallar, ganar y perder.
//
//     node scripts/prueba_sonidosDelJuego.mjs        (desde frontend/)
//
// POR QUÉ SON CUATRO Y POR QUÉ SON LOS MISMOS EN TODA LA CASA. Antes cada juego elegía los
// suyos de la caja común: uno usaba `ping` y `thud`, otro `chime` y `deny`, otro `special` y
// `fanfare`. La misma cosa —acertar— sonaba distinta en cada sitio, así que ninguno de los
// sonidos llegaba a significar nada: **un tono agudo sólo quiere decir «bien» cuando SIEMPRE
// quiere decir «bien»**. Gabri, el 27 de septiembre de 2026: *«a veces se pasa a la siguiente y
// no queda claro si lo has hecho bien o mal… unificaría los sonidos de victoria y derrota de
// todos los minijuegos, ¿no?»*.
//
// Y LA MITAD DE LOS SILENCIOS NO ERAN DE DISEÑO, ERAN UN ERROR MUDO: `sound.thud` no existe ni
// ha existido nunca, y el Árbol de las Lenguas lo llamaba al fallar. El `?.` se lo tragaba sin
// error y sin aviso, así que **fallar en el Árbol no ha sonado jamás**. Ahora lo vigila
// `tools/check_sonidos.py`, que comprueba que todo `sound.loQueSea()` exista de verdad.
//
// CÓMO SUENAN, Y POR QUÉ ASÍ:
//
//  · **Acertar sube y fallar baja.** Es la única convención que no hay que aprender.
//  · **El fallo no castiga**: dos notas graves y cortas, sin ruido ni distorsión. En esta casa
//    fallar enseña y la pregunta vuelve a salir; un zumbido de error dice lo contrario.
//  · **Ganar y perder duran el triple.** Lo que distingue el final de un paso más no es el
//    tono, es que ocupe: si el último acierto sonara como los otros seis, el momento que da
//    sentido a la partida llegaría sin decirlo.
//
// Y VIVEN AQUÍ Y NO EN `sound.js` porque son NÚMEROS —notas, duraciones y volúmenes—, y en esta
// casa los números viven en el lib y quien los toca vive en su sitio. De paso, se pueden probar
// sin abrir un navegador.
//
// Lógica pura, sin audio.

/** Do, mi, sol… las notas que hacen falta, en hercios. Escritas con nombre para poder leerlas. */
const LA3 = 220.0;
const DO4 = 261.63;
const MIb4 = 311.13;
const FA4 = 349.23;
const LA4 = 440.0;
const DO5 = 523.25;
const RE5 = 587.33;
const MI5 = 659.25;
const SOL5 = 783.99;
const LA5 = 880.0;
const DO6 = 1046.5;

/**
 * LO QUE SUENA EN CADA CASO: una lista de notas con cuándo entra cada una.
 *
 * TODAS SON `triangle` menos las colas, que son `sine`. El triángulo tiene cuerpo y se oye por
 * encima de la música; la senoidal no, y por eso sirve para lo que se queda sonando debajo sin
 * pelear con nada.
 */
export const DEL_JUEGO = {
  acierto: [
    { freq: RE5, type: "triangle", dur: 0.11, gain: 0.11 },
    { freq: LA5, type: "triangle", dur: 0.17, gain: 0.1, when: 0.08 },
  ],
  fallo: [
    { freq: MIb4, type: "triangle", dur: 0.12, gain: 0.09 },
    { freq: LA3, type: "triangle", dur: 0.22, gain: 0.08, when: 0.09 },
  ],
  // MÁS ALTO SIN PASAR DEL TECHO (Gabri, 27 de octubre de 2026: *«un poco más alto»*): cada
  // nota llega al 0,12 de la casa y la última lleva debajo su octava, que suma cuerpo sin pico.
  ganas: [
    { freq: DO5, type: "triangle", dur: 0.18, gain: 0.12 },
    { freq: MI5, type: "triangle", dur: 0.18, gain: 0.12, when: 0.09 },
    { freq: SOL5, type: "triangle", dur: 0.18, gain: 0.12, when: 0.18 },
    { freq: DO6, type: "triangle", dur: 0.55, gain: 0.12, when: 0.27 },
    { freq: SOL5, type: "sine", dur: 0.55, gain: 0.09, when: 0.27 },
    { freq: DO5, type: "sine", dur: 0.55, gain: 0.08, when: 0.27 },
  ],
  pierdes: [
    { freq: LA4, type: "triangle", dur: 0.2, gain: 0.09 },
    { freq: FA4, type: "triangle", dur: 0.2, gain: 0.09, when: 0.12 },
    { freq: DO4, type: "triangle", dur: 0.2, gain: 0.09, when: 0.24 },
    { freq: 174.61, type: "sine", dur: 0.45, gain: 0.07, when: 0.36 },
  ],
};

/** Los cuatro nombres, para quien tenga que recorrerlos. */
/**
 * LAS DOS VOCES DEL ENCUENTRO CON ASTERIÓN, y entran en este banco el 3 de octubre de 2026.
 *
 * Están aquí y no en `sound.js` por lo mismo que los cuatro de arriba: **las notas son datos**
 * y un fichero de notas se lee de un vistazo. Lo pidió Gabri al ver que la cámara se daba la
 * vuelta en el fondo sin que nada dijera por qué: *«que aparezca entre las sombras el Minotauro
 * y se ponga algún sonido de chillido o grito del Minotauro como bestia y de la Estrella como
 * víctima asustada; si no, no se entiende»*.
 *
 * SON OPUESTAS EN TODO, Y DE AHÍ SALEN LAS NOTAS. El rugido es **grave y cae** —110 hasta 38,
 * en sierra, que es lo que da garganta— y es **largo**: lo que hace que algo se lea como grande
 * no es el volumen, es que tarde. El susto es **agudo y sube**, en dos golpes cortos y limpios,
 * y entra con retardo: primero se ve al bicho y luego se reacciona. Un grito de susto no es un
 * rugido pequeño; es otra cosa.
 */
DEL_JUEGO.rugido = [
  { freq: 110, to: 38, type: "sawtooth", dur: 0.9, gain: 0.13 },
  { freq: 74, to: 41, type: "square", dur: 0.75, gain: 0.07, when: 0.05 },
];
DEL_JUEGO.susto = [
  { freq: 880, to: 1320, type: "triangle", dur: 0.1, gain: 0.08, when: 0.26 },
  { freq: 1180, to: 1760, type: "triangle", dur: 0.14, gain: 0.07, when: 0.38 },
];

/** Los CUATRO del minijuego, que siguen siendo cuatro: el encuentro de Asterión usa el mismo
 *  banco pero no es uno de ellos —no celebra, no avisa y sólo suena una vez por partida—. */
export const LOS_CUATRO = ["acierto", "fallo", "ganas", "pierdes"];

/** Cuánto dura un sonido de éstos, de principio a fin. Lo usa la prueba para comprobar que
 *  ganar y perder ocupan de verdad más que acertar y fallar. */
export const loQueDura = (cual) =>
  Math.max(...DEL_JUEGO[cual].map((n) => (n.when || 0) + n.dur));

export default DEL_JUEGO;
